import { flechaEm } from '../../dominio/tabletop/flexaoEscadaGeometria'
import { useMemo } from 'react'
import * as THREE from 'three'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { FitaTensionada } from '../cordas/FitaTensionada'
import { ESPACO_DEGRAUS_ESCADA_M, GEOMETRIA_DESCENSOR, GEOMETRIA_PONTO_ANCORAGEM } from '../../dominio/tabletop/ancoragem'
import { disposicaoDoSistema, ESPACO_ENTRE_TRECHOS_M } from '../../dominio/tabletop/vantagemMecanica'
import { SetaCone } from './Cordas3D'
import { PoliaRealista, RoldanaComCanal } from './Roldana3D'

/**
 * Objetos de cordas e salvamento terrestre, desenhados de forma procedural.
 *
 * As coordenadas aqui são as mesmas de `dominio/tabletop/ancoragem.ts`: o pé da
 * escada, a base do toco e o olhal da maca estão na origem de cada objeto, e os
 * pontos de ancoragem do domínio caem sobre estas peças. Ao mudar uma medida
 * aqui, confira o ponto correspondente lá.
 */

const COR_ACO = '#94a3b8'
const COR_AMARELO = '#facc15'

/**
 * Esfera invisível que aumenta a área de toque dos equipamentos pequenos: uma
 * polia de 10 cm não pode exigir pontaria de milímetro para ser selecionada.
 */
function AlvoDeToque({ raio = 0.22 }: { raio?: number }) {
  return (
    <mesh>
      <sphereGeometry args={[raio, 8, 6]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  )
}

/**
 * Escada de alumínio ou fibra, em pé sobre o ponto onde foi colocada. O pé fica
 * na origem e ela sobe inclinada para +Z: 90° é vertical, 0° é deitada.
 */
export function EscadaTabletop({ anguloGraus, comprimentoM, flexaoM = 0 }: { anguloGraus: number; comprimentoM: number; flexaoM?: number }) {
  const degraus = Math.floor(comprimentoM / ESPACO_DEGRAUS_ESCADA_M + 1e-9)
  // a flexão curva a escada no plano dela (eixo z do grupo = para onde o peso a empurra)
  const flecha = (s: number) => flechaEm(flexaoM, comprimentoM, s)
  const trechos = flexaoM !== 0 ? TRECHOS_DO_BANZO : 1
  const banzos = Array.from({ length: trechos }, (_, k) => {
    const s0 = (k / trechos) * comprimentoM
    const s1 = ((k + 1) / trechos) * comprimentoM
    const comprimento = s1 - s0
    const inclinacao = Math.atan2(flecha(s1) - flecha(s0), comprimento)
    return { k, s: (s0 + s1) / 2, comprimento: Math.hypot(comprimento, flecha(s1) - flecha(s0)), inclinacao }
  })
  return (
    <group rotation={[Math.PI / 2 - (anguloGraus * Math.PI) / 180, 0, 0]}>
      {[-0.25, 0.25].map((x) =>
        banzos.map((t) => (
          <mesh key={`${x}-${t.k}`} position={[x, t.s, flecha(t.s)]} rotation={[t.inclinacao, 0, 0]}>
            <CaixaArredondada args={[0.05, t.comprimento + 0.004, 0.035]} />
            <meshStandardMaterial color="#ea580c" roughness={0.45} metalness={0.3} />
          </mesh>
        )),
      )}
      {Array.from({ length: degraus }, (_, i) => (
        <mesh key={i} position={[0, (i + 1) * ESPACO_DEGRAUS_ESCADA_M, flecha((i + 1) * ESPACO_DEGRAUS_ESCADA_M)]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.016, 0.016, 0.5, 8]} />
          <meshStandardMaterial color="#cbd5e1" metalness={0.6} roughness={0.35} />
        </mesh>
      ))}
      {[-0.25, 0.25].map((x) => (
        <mesh key={`sapata-${x}`} position={[x, 0.02, 0]}>
          <CaixaArredondada args={[0.07, 0.04, 0.06]} />
          <meshStandardMaterial color="#1e293b" roughness={0.9} />
        </mesh>
      ))}
    </group>
  )
}

/** Em quantos pedaços retos cada banzo é desenhado quando a escada flexiona. */
const TRECHOS_DO_BANZO = 10

/** Toco de árvore com a fita vermelha de ancoragem dando a volta no tronco, perto do chão. */
export function TocoTabletop({ alturaM, diametroM }: { alturaM: number; diametroM: number }) {
  const raio = diametroM / 2
  return (
    <group>
      <mesh position={[0, alturaM / 2, 0]}>
        <cylinderGeometry args={[raio * 0.92, raio, alturaM, 16]} />
        <meshStandardMaterial color="#7c5a3c" roughness={1} />
      </mesh>
      {/* raízes: base alargada */}
      <mesh position={[0, 0.04, 0]}>
        <cylinderGeometry args={[raio * 1.05, raio * 1.3, 0.08, 16]} />
        <meshStandardMaterial color="#6b4a2f" roughness={1} />
      </mesh>
      {/* topo serrado, mais claro */}
      <mesh position={[0, alturaM + 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[raio * 0.92, 20]} />
        <meshStandardMaterial color="#c9a27a" roughness={0.9} />
      </mesh>
      {/* fita de ancoragem */}
      <mesh position={[0, Math.min(0.25, alturaM / 2), 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[raio * 1.02, 0.02, 8, 24]} />
        <meshStandardMaterial color="#dc2626" roughness={0.8} />
      </mesh>
    </group>
  )
}

const CANTOS_MACA: ReadonlyArray<readonly [number, number, number]> = [
  [-0.3, 0.12, -0.6],
  [0.3, 0.12, -0.6],
  [-0.3, 0.12, 0.6],
  [0.3, 0.12, 0.6],
]
const OLHAL_MACA: readonly [number, number, number] = [0, 0.75, 0]

/** Maca de resgate amarela com o triângulo de içamento: quatro fitas do casco até o olhal central. */
export function MacaTabletop() {
  return (
    <group>
      <mesh position={[0, 0.06, 0]}>
        <CaixaArredondada args={[0.6, 0.06, 2.1]} />
        <meshStandardMaterial color={COR_AMARELO} roughness={0.5} />
      </mesh>
      {[-0.28, 0.28].map((x) => (
        <mesh key={x} position={[x, 0.15, 0]}>
          <CaixaArredondada args={[0.04, 0.16, 2.1]} />
          <meshStandardMaterial color={COR_AMARELO} roughness={0.5} />
        </mesh>
      ))}
      {[-0.7, 0, 0.7].map((z) => (
        <mesh key={z} position={[0, 0.1, z]}>
          <CaixaArredondada args={[0.62, 0.012, 0.05]} />
          <meshStandardMaterial color="#1e293b" roughness={0.9} />
        </mesh>
      ))}
      {CANTOS_MACA.map((canto, i) => (
        <FitaTensionada key={i} de={[canto[0], canto[1], canto[2]]} para={[...OLHAL_MACA]} cor="#fde047" largura={0.03} />
      ))}
      <mesh position={[0, 0.78, 0]} rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.035, 0.007, 8, 16]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.8} roughness={0.3} />
      </mesh>
    </group>
  )
}

/**
 * Polia de resgate: roldana com canal, placas laterais em gota (vermelhas) e o
 * olhal no alto das placas. A roldana gira em torno de Z; o olhal fica a 7,5 cm
 * do eixo, onde está o ponto `olhal` do domínio.
 */
export function PoliaTabletop({ giro = 0 }: { giro?: number }) {
  return (
    <group>
      <AlvoDeToque />
      <PoliaRealista raioM={0.035} alturaOlhalM={0.075} giros={[giro]} />
    </group>
  )
}

/**
 * O corpo do mosquetão em D, de uma ponta do gatilho à outra: o fundo, o
 * dorso reto (o lado forte, à esquerda), o alto e o nariz onde o gatilho
 * fecha. Medidas de um mosquetão de 10 cm; o centro fica na origem.
 */
const PERFIL_MOSQUETAO: ReadonlyArray<readonly [number, number]> = [
  [0.02, -0.038],
  [0.006, -0.05],
  [-0.014, -0.05],
  [-0.026, -0.038],
  [-0.0285, -0.018],
  [-0.0285, 0.018],
  [-0.026, 0.038],
  [-0.012, 0.05],
  [0.006, 0.05],
  [0.019, 0.042],
  [0.024, 0.031],
]
const GATILHO_MOSQUETAO = { de: [0.021, -0.035] as const, ate: [0.0235, 0.029] as const }

export function geometriaDoCorpoDoMosquetao(): THREE.TubeGeometry {
  const curva = new THREE.CatmullRomCurve3(PERFIL_MOSQUETAO.map(([x, y]) => new THREE.Vector3(x, y, 0)), false, 'centripetal')
  return new THREE.TubeGeometry(curva, 48, 0.0055, 10, false)
}

/**
 * Mosquetão de rosca em D: o corpo (dorso grosso), o gatilho mais fino e a
 * rosca da trava. `eixo` (no referencial da peça) é o eixo maior para onde ele
 * gira com a carga; sem ele, fica em pé.
 */
export function MosquetaoTabletop({ eixo, semAlvo = false }: { eixo?: readonly [number, number, number] | null; semAlvo?: boolean }) {
  const corpo = useMemo(geometriaDoCorpoDoMosquetao, [])
  const quaternion = useMemo(() => {
    const q = new THREE.Quaternion()
    if (eixo) q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(...eixo).normalize())
    return q
  }, [eixo])
  const [gx0, gy0] = GATILHO_MOSQUETAO.de
  const [gx1, gy1] = GATILHO_MOSQUETAO.ate
  const comprimentoGatilho = Math.hypot(gx1 - gx0, gy1 - gy0)
  const inclinacaoGatilho = -Math.atan2(gx1 - gx0, gy1 - gy0)
  return (
    <group>
      {!semAlvo && <AlvoDeToque raio={0.18} />}
      <group quaternion={quaternion} name="mosquetao-orientado">
        <mesh geometry={corpo}>
          <meshStandardMaterial color="#cbd5e1" metalness={0.85} roughness={0.28} />
        </mesh>
        <mesh position={[(gx0 + gx1) / 2, (gy0 + gy1) / 2, 0]} rotation={[0, 0, inclinacaoGatilho]}>
          <cylinderGeometry args={[0.0036, 0.0036, comprimentoGatilho, 8]} />
          <meshStandardMaterial color="#94a3b8" metalness={0.85} roughness={0.3} />
        </mesh>
        {/* a rosca da trava, perto do nariz */}
        <mesh position={[gx0 + (gx1 - gx0) * 0.72, gy0 + (gy1 - gy0) * 0.72, 0]} rotation={[0, 0, inclinacaoGatilho]}>
          <cylinderGeometry args={[0.0062, 0.0062, 0.022, 12]} />
          <meshStandardMaterial color="#f59e0b" metalness={0.6} roughness={0.35} />
        </mesh>
      </group>
    </group>
  )
}

/**
 * Ponto de ancoragem fixo (chapeleta com argola): a placa rente à superfície,
 * a cabeça do chumbador e a argola em pé, onde se clipa o mosquetão.
 */
export function PontoAncoragemTabletop() {
  const g = GEOMETRIA_PONTO_ANCORAGEM
  return (
    <group>
      <AlvoDeToque raio={0.16} />
      <mesh position={[0, g.placa[1] / 2, 0]}>
        <CaixaArredondada args={[...g.placa]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.8} roughness={0.35} />
      </mesh>
      {/* chumbador: a cabeça sextavada numa ponta da placa */}
      <mesh position={[-g.placa[0] * 0.4, g.placa[1] + 0.005, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.01, 6]} />
        <meshStandardMaterial color="#64748b" metalness={0.9} roughness={0.3} />
      </mesh>
      <mesh position={[0, g.centroArgolaY, 0]}>
        <torusGeometry args={[g.raioArgolaM, g.tuboArgolaM, 10, 28]} />
        <meshStandardMaterial color="#e2e8f0" metalness={0.9} roughness={0.22} />
      </mesh>
    </group>
  )
}

/**
 * Alça/vértice de fita (ponto de ancoragem têxtil): o laço dobrado de fita costurada
 * onde se clipa um mosquetão, se passa uma corda ou se pendura a polia.
 */
export function AlcaFitaTabletop() {
  return (
    <group name="alca-fita">
      <AlvoDeToque raio={0.18} />
      {/* laço circular de fita tubular/chata */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.04, 0.012, 12, 32]} />
        <meshStandardMaterial color="#ea580c" roughness={0.7} />
      </mesh>
      {/* costura de segurança reforçada da alça (tarja escura) */}
      <mesh position={[0, -0.038, 0]}>
        <boxGeometry args={[0.028, 0.016, 0.016]} />
        <meshStandardMaterial color="#9a3412" roughness={0.8} />
      </mesh>
      {/* anel interno / olhal de passagem suave para o mosquetão */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.024, 0.003, 8, 24]} />
        <meshStandardMaterial color="#cbd5e1" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  )
}

/** Bloqueador (ascensor): o corpo amarelo com o canal da corda e o olhal embaixo. */
export function BloqueadorTabletop() {
  // a corda corre em x = 0 (entrada em cima, saída embaixo); a armação fica ao lado dela
  return (
    <group>
      <AlvoDeToque />
      {/* a armação de alumínio, atrás e ao lado da corda */}
      <mesh position={[0.016, 0, -0.011]}>
        <CaixaArredondada args={[0.05, 0.155, 0.006]} />
        <meshStandardMaterial color="#475569" metalness={0.6} roughness={0.35} />
      </mesh>
      {/* a borda dobrada que forma o canal da corda */}
      <mesh position={[-0.011, 0.005, -0.002]}>
        <CaixaArredondada args={[0.005, 0.11, 0.022]} />
        <meshStandardMaterial color="#475569" metalness={0.6} roughness={0.35} />
      </mesh>
      {/* o came dentado que aperta a corda contra o canal */}
      <mesh position={[0.012, 0.018, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.016, 14]} />
        <meshStandardMaterial color="#111827" metalness={0.5} roughness={0.45} />
      </mesh>
      {[0, 1, 2, 3].map((k) => (
        <mesh key={k} position={[0.0005, 0.008 + k * 0.007, 0]}>
          <boxGeometry args={[0.004, 0.003, 0.014]} />
          <meshStandardMaterial color="#111827" metalness={0.5} roughness={0.45} />
        </mesh>
      ))}
      {/* a trava de segurança do came */}
      <mesh position={[0.03, 0.03, 0.006]} rotation={[0, 0, -0.5]}>
        <CaixaArredondada args={[0.008, 0.03, 0.006]} />
        <meshStandardMaterial color={COR_AMARELO} roughness={0.5} />
      </mesh>
      {/* os furos de cima e de baixo (o de baixo é o olhal) */}
      <mesh position={[0.016, 0.065, -0.011]}>
        <torusGeometry args={[0.009, 0.003, 8, 14]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[0, -0.075, 0]}>
        <torusGeometry args={[0.012, 0.004, 8, 14]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.8} roughness={0.3} />
      </mesh>
    </group>
  )
}

/**
 * Descensor. No oito, dois anéis (o grande, por onde a corda passa, e o
 * pequeno, do olhal) ligados pelo pescoço; no autoblocante (tipo ID), a
 * placa com o came e a alavanca laranja. As medidas são as de
 * `GEOMETRIA_DESCENSOR`, onde ficam os pontos.
 */
export function DescensorTabletop({ autoblocante }: { autoblocante: boolean }) {
  if (autoblocante) {
    const g = GEOMETRIA_DESCENSOR.autoblocante
    return (
      <group position={[0, GEOMETRIA_DESCENSOR.base.autoblocante, 0]}>
        <AlvoDeToque />
        <mesh position={[0, 0, -0.008]}>
          <CaixaArredondada args={[0.065, 0.16, 0.008]} />
          <meshStandardMaterial color="#64748b" metalness={0.55} roughness={0.4} />
        </mesh>
        <mesh position={[0, 0, 0.008]}>
          <CaixaArredondada args={[0.06, 0.14, 0.006]} />
          <meshStandardMaterial color="#334155" metalness={0.5} roughness={0.45} />
        </mesh>
        {/* o came por onde a corda passa */}
        <mesh position={[0.008, 0.015, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.018, 0.018, 0.02, 16]} />
          <meshStandardMaterial color="#111827" metalness={0.4} roughness={0.5} />
        </mesh>
        {/* a alavanca de descida */}
        <mesh position={[0.045, 0.045, 0.012]} rotation={[0, 0, -0.35]}>
          <CaixaArredondada args={[0.012, 0.08, 0.008]} />
          <meshStandardMaterial color="#f97316" roughness={0.5} />
        </mesh>
        <mesh position={[g.olhal[0], g.olhal[1], 0]}>
          <torusGeometry args={[0.013, 0.004, 8, 14]} />
          <meshStandardMaterial color={COR_ACO} metalness={0.8} roughness={0.3} />
        </mesh>
      </group>
    )
  }
  const g = GEOMETRIA_DESCENSOR.oito
  return (
    <group position={[0, GEOMETRIA_DESCENSOR.base.oito, 0]}>
      <AlvoDeToque />
      <mesh position={[0, 0.045, 0]}>
        <torusGeometry args={[0.032, 0.008, 10, 28]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.75} roughness={0.3} />
      </mesh>
      <mesh position={[0, -0.016, 0]}>
        <CaixaArredondada args={[0.022, 0.03, 0.012]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.75} roughness={0.3} />
      </mesh>
      <mesh position={[g.olhal[0], g.olhal[1], 0]}>
        <torusGeometry args={[0.016, 0.007, 10, 22]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.75} roughness={0.3} />
      </mesh>
    </group>
  )
}

/**
 * Protetor de corda: a manga acolchoada dobrada na quina, metade deitada no
 * topo (+Z local, para dentro) e metade descendo a face (−Y). A corda passa
 * pela origem, a quina.
 */
export function ProtetorCordaTabletop({ comprimentoM }: { comprimentoM: number }) {
  const meio = comprimentoM / 2
  const cor = '#1f2937'
  return (
    <group>
      <AlvoDeToque raio={0.25} />
      <mesh position={[0, 0.03, meio / 2]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.035, 0.035, meio, 12]} />
        <meshStandardMaterial color={cor} roughness={0.9} />
      </mesh>
      <mesh position={[0, -meio / 2, -0.03]}>
        <cylinderGeometry args={[0.035, 0.035, meio, 12]} />
        <meshStandardMaterial color={cor} roughness={0.9} />
      </mesh>
      {/* a dobra na quina */}
      <mesh position={[0, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.03, 0.035, 10, 12, Math.PI / 2]} />
        <meshStandardMaterial color={cor} roughness={0.9} />
      </mesh>
      {/* as faixas de velcro, em laranja */}
      {[meio * 0.4, meio * 0.8].map((d) => (
        <mesh key={d} position={[0, 0.03, d]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.037, 0.037, 0.03, 12]} />
          <meshStandardMaterial color="#f97316" roughness={0.8} />
        </mesh>
      ))}
    </group>
  )
}

/** Nó oito: o laço da corda dobrado em oito, só visual. O ponto de passagem fica no cruzamento. */
export function NoOitoTabletop({ cor = '#facc15' }: { cor?: string }) {
  return (
    <group>
      <AlvoDeToque raio={0.14} />
      {[-0.035, 0.035].map((y) => (
        <mesh key={y} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.032, 0.009, 8, 20]} />
          <meshStandardMaterial color={cor} roughness={0.8} />
        </mesh>
      ))}
      <mesh position={[0, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.009, 0.009, 0.08, 8]} />
        <meshStandardMaterial color={cor} roughness={0.8} />
      </mesh>
    </group>
  )
}

/**
 * Nó fiel (volta do fiel): duas voltas cruzadas em torno de uma barra, com a
 * ponta da corda saindo para o lado. A origem é o centro da barra abraçada
 * (o degrau ou o banzo); a barra fica ao longo de X.
 */
export function NoFielTabletop({ cor = '#facc15' }: { cor?: string }) {
  return (
    <group>
      <AlvoDeToque raio={0.12} />
      {[-0.012, 0.012].map((x, i) => (
        <mesh key={x} position={[x, 0, 0]} rotation={[0, Math.PI / 2 + (i === 0 ? 0.35 : -0.35), 0]}>
          <torusGeometry args={[0.024, 0.008, 8, 20]} />
          <meshStandardMaterial color={cor} roughness={0.8} />
        </mesh>
      ))}
      {/* a ponta que sobra, saindo do cruzamento */}
      <mesh position={[0, -0.04, 0.02]} rotation={[0.5, 0, 0]}>
        <cylinderGeometry args={[0.008, 0.008, 0.08, 8]} />
        <meshStandardMaterial color={cor} roughness={0.8} />
      </mesh>
    </group>
  )
}

/**
 * Ponte baixa de laje com vigas, como as do canal: a origem é o centro do
 * tabuleiro, no nível do topo; o vão corre pelo eixo Z e a largura pelo X. As
 * vigas ficam sob a laje, o guarda-corpo nas duas bordas e o pilar (opcional)
 * no meio do vão. As medidas são as mesmas de `pontosDaPonte` em `ancoragem.ts`.
 */
export function PonteTabletop({
  vaoM,
  larguraM,
  espessuraM,
  guardaCorpoM,
  pilarAlturaM,
}: {
  vaoM: number
  larguraM: number
  espessuraM: number
  guardaCorpoM: number
  pilarAlturaM: number
}) {
  const laje = Math.min(0.3, espessuraM)
  const alturaViga = Math.max(0, espessuraM - laje)
  const xVigas = [-1, -1 / 3, 1 / 3, 1].map((f) => f * Math.max(0, larguraM / 2 - 0.6))
  const postes = Math.max(2, Math.round(vaoM / 2) + 1)
  return (
    <group>
      <mesh position={[0, -laje / 2, 0]}>
        <CaixaArredondada args={[larguraM, laje, vaoM]} />
        <meshStandardMaterial color="#a1a1aa" roughness={0.95} />
      </mesh>
      {alturaViga > 0 &&
        xVigas.map((x) => (
          <mesh key={x} position={[x, -laje - alturaViga / 2, 0]}>
            <CaixaArredondada args={[0.3, alturaViga, vaoM]} />
            <meshStandardMaterial color="#8d8d96" roughness={0.95} />
          </mesh>
        ))}
      {guardaCorpoM > 0 &&
        [-larguraM / 2, larguraM / 2].map((x) => (
          <group key={x}>
            <mesh position={[x, guardaCorpoM, 0]}>
              <boxGeometry args={[0.08, 0.08, vaoM]} />
              <meshStandardMaterial color={COR_ACO} metalness={0.6} roughness={0.4} />
            </mesh>
            {Array.from({ length: postes }, (_, i) => (
              <mesh key={i} position={[x, guardaCorpoM / 2, -vaoM / 2 + (vaoM / (postes - 1)) * i]}>
                <boxGeometry args={[0.07, guardaCorpoM, 0.07]} />
                <meshStandardMaterial color={COR_ACO} metalness={0.6} roughness={0.4} />
              </mesh>
            ))}
          </group>
        ))}
      {pilarAlturaM > 0 && (
        <mesh position={[0, -espessuraM - pilarAlturaM / 2, 0]}>
          <CaixaArredondada args={[larguraM * 0.6, pilarAlturaM, 0.8]} />
          <meshStandardMaterial color="#8d8d96" roughness={0.95} />
        </mesh>
      )}
    </group>
  )
}

/** Polia dupla: duas roldanas lado a lado, três placas (a do meio entre elas) e um olhal só. O eixo das roldanas é Z. */
export function PoliaDuplaTabletop({ giros = [] }: { giros?: readonly number[] }) {
  return (
    <group>
      <AlvoDeToque />
      <PoliaRealista raioM={0.035} alturaOlhalM={0.075} roldanasZ={[-0.022, 0.022]} giros={giros} />
    </group>
  )
}

const COR_CORDA_SISTEMA = '#facc15'
const ESPESSURA_TRECHO_M = 0.014

/**
 * Sistema de vantagem mecânica (talha): o bloco fixo em x = 0, o móvel em
 * x = curso, os trechos de corda entre eles, as roldanas e a ponta livre, por
 * onde se puxa, para o lado negativo de X. Cada trecho leva uma seta pequena
 * no sentido da força (puxa o bloco móvel para o fixo) e a ponta livre leva a
 * seta grande de puxada. A disposição vem de `disposicaoDoSistema`, a mesma que
 * o cálculo e os pontos de ancoragem usam.
 */
const COR_FUSIVEL = '#f97316'

/**
 * O fusível ou a trava do sistema, na corda de içamento logo depois do bloco móvel: o
 * Prusik é um laço de cordelete enrolado na corda (quatro voltas e o laço que
 * sobra); o clássico é um cordelete curto em série, com um nó em cada ponta.
 */
function FusivelDoSistema({ tipo, x }: { tipo: 'prusik' | 'classico' | 'freioOito' | 'freioOitoTravado'; x: number }) {
  if (tipo === 'freioOito' || tipo === 'freioOitoTravado') {
    return (
      <group position={[x + 0.04, 0, 0]} rotation={[0, 0, Math.PI / 2]} name="trava-freio-oito">
        <DescensorTabletop autoblocante={false} />
        {/* passagem travada: a volta extra da corda na orelha do freio */}
        {tipo === 'freioOitoTravado' && (
          <mesh position={[0, 0.07, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.014, 0.005, 8, 14]} />
            <meshStandardMaterial color={COR_CORDA_SISTEMA} roughness={0.8} />
          </mesh>
        )}
      </group>
    )
  }
  if (tipo === 'prusik') {
    return (
      <group position={[x, 0, 0]} name="fusivel-prusik">
        {[0, 0.014, 0.028, 0.042].map((dx) => (
          <mesh key={dx} position={[dx, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
            <torusGeometry args={[0.02, 0.006, 8, 16]} />
            <meshStandardMaterial color={COR_FUSIVEL} roughness={0.8} />
          </mesh>
        ))}
        {/* o laço de cordelete que sobra, pendurado */}
        <mesh position={[0.021, -0.07, 0]} rotation={[0, 0, 0]}>
          <torusGeometry args={[0.04, 0.005, 8, 18]} />
          <meshStandardMaterial color={COR_FUSIVEL} roughness={0.8} />
        </mesh>
      </group>
    )
  }
  return (
    <group position={[x, 0, 0]} name="fusivel-classico">
      <mesh position={[0.07, 0, 0]}>
        <boxGeometry args={[0.14, 0.014, 0.014]} />
        <meshStandardMaterial color="#dc2626" roughness={0.8} />
      </mesh>
      {[0, 0.14].map((dx) => (
        <mesh key={dx} position={[dx, 0, 0]}>
          <sphereGeometry args={[0.02, 10, 8]} />
          <meshStandardMaterial color={COR_FUSIVEL} roughness={0.8} />
        </mesh>
      ))}
    </group>
  )
}

export function SistemaVMTabletop({ razao, cursoM, fusivel = 0 }: { razao: number; cursoM: number; fusivel?: number }) {
  const d = disposicaoDoSistema(razao, cursoM)
  const { pontaLivre, pontaMorta } = d
  const placa = (faixa: readonly [number, number], x: number, chave: string) => (
    <mesh key={chave} position={[x, 0, (faixa[0] + faixa[1]) / 2]}>
      <CaixaArredondada args={[0.03, 0.12, faixa[1] - faixa[0] + 0.03]} />
      <meshStandardMaterial color={COR_AMARELO} metalness={0.4} roughness={0.5} />
    </mesh>
  )
  return (
    <group>
      <AlvoDeToque raio={Math.max(0.3, cursoM / 2)} />
      {d.trechos.map((t) => {
        const meio = (t.de[0] + t.para[0]) / 2
        return (
          <group key={t.indice}>
            <mesh position={[meio, 0, t.de[1]]}>
              <boxGeometry args={[Math.abs(t.para[0] - t.de[0]), ESPESSURA_TRECHO_M, ESPESSURA_TRECHO_M]} />
              <meshStandardMaterial color={COR_CORDA_SISTEMA} roughness={0.7} />
            </mesh>
            {/* a força deste trecho puxa o bloco móvel para o fixo (-X) */}
            <SetaCone posicao={[cursoM * 0.5, 0.04, t.de[1]]} direcao={[-1, 0, 0]} cor={COR_CORDA_SISTEMA} tamanho={0.55} />
          </group>
        )
      })}
      {/* roldanas com canal, deitadas: o eixo é Y, a corda corre no plano XZ */}
      {d.roldanas.map((r, i) => (
        <group key={i} position={[r.x, 0, r.z]} rotation={[Math.PI / 2, 0, 0]}>
          <RoldanaComCanal raioM={ESPACO_ENTRE_TRECHOS_M / 2} espessuraM={0.035} />
        </group>
      ))}
      {fusivel === 1 && <FusivelDoSistema tipo="prusik" x={cursoM + 0.14} />}
      {fusivel === 2 && <FusivelDoSistema tipo="classico" x={cursoM + 0.14} />}
      {fusivel === 3 && <FusivelDoSistema tipo="freioOito" x={cursoM + 0.14} />}
      {fusivel === 4 && <FusivelDoSistema tipo="freioOitoTravado" x={cursoM + 0.14} />}
      {placa(d.faixaFixo, 0, 'bloco-fixo')}
      {placa(d.faixaMovel, cursoM, 'bloco-movel')}
      {/* olhais: o do bloco fixo (ancoragem) e o do móvel (carga) */}
      <mesh position={[-0.04, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.03, 0.007, 8, 16]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[cursoM + 0.04, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.03, 0.007, 8, 16]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.8} roughness={0.3} />
      </mesh>
      {/* a ponta de trás, presa */}
      <mesh position={[pontaMorta.x, 0, pontaMorta.z]}>
        <sphereGeometry args={[0.025, 10, 8]} />
        <meshStandardMaterial color="#dc2626" roughness={0.6} />
      </mesh>
      {/* a ponta livre: sai do bloco fixo para longe da carga, com a seta de puxada */}
      <mesh position={[(pontaLivre.x + pontaLivre.ateX) / 2, 0, pontaLivre.z]}>
        <boxGeometry args={[Math.abs(pontaLivre.ateX - pontaLivre.x), ESPESSURA_TRECHO_M, ESPESSURA_TRECHO_M]} />
        <meshStandardMaterial color={COR_CORDA_SISTEMA} roughness={0.7} />
      </mesh>
      <SetaCone posicao={[pontaLivre.ateX - 0.12, 0, pontaLivre.z]} direcao={[-1, 0, 0]} cor="#fde047" tamanho={1.8} />
    </group>
  )
}
